import {
  rankWith,
  and,
  isStringControl,
  isNumberControl,
  isBooleanControl,
  isIntegerControl,
  formatIs,
  isObjectControl,
  isEnumControl,
  isPrimitiveArrayControl,
  UISchemaElement,
  JsonSchema,
  TesterContext,
  ControlElement,
  resolveSchema,
  schemaMatches,
  schemaTypeIs,
} from '@jsonforms/core';
import { type AnnotatedJsonSchema, URI_FORMATS } from '@web-config-app/core';
import {
  isPrimitiveObjectSchema,
  isCombinatorObjectSchema,
  isCombinatorArraySchema,
  isRelationshipEntityReference,
} from '@web-config-app/schema-utils';
import { TesterRank } from '@web-config-app/entity-form';

/**
 * To find a matching renderer, JSON Forms relies on so-called testers. Every renderer has a tester associated with its registration, which is a function of a UI schema and a JSON schema returning a number. The returned number is the priority which expresses if and how well a renderer can actually render the given UI Schema Element (where NOT_APPLICABLE aka. -1 means "not at all").
 */

/**
 * JsonSchema testers use a `rank` to break ties in instances where multiple testers return `true` for the
 * same property. As an example, for a property with `type: 'string'` and `format: 'date'`, both the
 * `isTextInputControl` and `isDateInputControl` testers below will return true. By giving `isDateInputControl`
 * a higher rank, it will be the matcher used to return the property renderer.
 */

/**
 * Internal tester helpers
 */

const isCombinatorArrayOfComplexObjects = (
  uischema: UISchemaElement,
  schema: JsonSchema,
  context: TesterContext,
) => {
  const schemaPath = (uischema as ControlElement).scope;
  const resolvedSchema = resolveSchema(
    schema,
    schemaPath,
    context?.rootSchema ?? schema,
  );
  const { oneOf } = resolvedSchema;
  return Array.isArray(oneOf)
    ? oneOf.some(
        (subSchema: AnnotatedJsonSchema) => !isPrimitiveObjectSchema(subSchema),
      )
    : false;
};

/**
 * Primitive Control Testers
 */

// type: string
export const isTextInputControl = rankWith(TesterRank.Base, isStringControl);
export const isTextareaControl = rankWith(
  TesterRank.Higher,
  and(
    isStringControl,
    schemaMatches(
      (schema) =>
        (schema as AnnotatedJsonSchema)['x-entity-control']?.type ===
        'textarea',
    ),
  ),
);

export const isArrayTextareaControl = rankWith(
  TesterRank.Highest,
  and(
    isPrimitiveArrayControl,
    schemaMatches(
      (schema) =>
        (schema as AnnotatedJsonSchema)['x-entity-control']?.type ===
        'arrayTextarea',
    ),
  ),
);

export const isGeneratedIdControl = rankWith(
  TesterRank.Higher,
  and(
    isStringControl,
    schemaMatches(
      (schema) =>
        (schema as AnnotatedJsonSchema)['x-entity-generate']?.type === 'id',
    ),
  ),
);

export const isDateInputControl = rankWith(
  TesterRank.Higher,
  // has type: string AND format: `date`
  and(isStringControl, formatIs('date')),
);

export const isDateTimeInputControl = rankWith(
  TesterRank.Higher,
  // has type: string AND format: date-time
  and(isStringControl, formatIs('date-time')),
);

export const isRichTextInputControl = rankWith(
  TesterRank.Higher,
  // has type: string AND format: rich-text
  and(isStringControl, formatIs('rich-text')),
);

export const isJsonInputControl = rankWith(
  TesterRank.Higher,
  // has type: string AND format: json
  and(isStringControl, formatIs('json')),
);

export const isCodeEditorControl = rankWith(
  TesterRank.Higher,
  // has type: string AND 'x-entity-control': {type: 'codeEditor'} annotation
  and(
    isStringControl,
    schemaMatches(
      (schema: AnnotatedJsonSchema) =>
        schema['x-entity-control']?.type === 'codeEditor',
    ),
  ),
);

export const isRuleCombinationJsonLogicInputControl = rankWith(
  TesterRank.Higher,
  // has type: string AND 'x-entity-control': { type: 'json-logic' }
  and(
    isStringControl,
    schemaMatches(
      (schema: AnnotatedJsonSchema) =>
        schema['x-entity-control']?.type === 'json-logic',
    ),
  ),
);

export const isDataSourceEnumControl = rankWith(
  TesterRank.Higher,
  (uischema: UISchemaElement, schema: JsonSchema, context: TesterContext) => {
    const schemaPath = (uischema as ControlElement).scope;
    const resolvedSchema = resolveSchema(
      schema,
      schemaPath,
      context?.rootSchema ?? schema,
    ) as AnnotatedJsonSchema;
    const dataSourceType = resolvedSchema['x-entity-data-source']?.type;
    return Boolean(
      dataSourceType === 'enumOptions' ||
        dataSourceType === 'groupedEnumOptions',
    );
  },
);

export const isSelectInputControl = rankWith(TesterRank.Higher, isEnumControl);

export const isRuleTemplateJsonLogicInputControl = rankWith(
  TesterRank.Higher + 1,
  // has type: string AND 'x-entity-control': { type: 'json-logic', options.jsonLogic.isRuleTemplate: true }
  and(
    isStringControl,
    (uischema: UISchemaElement, schema: JsonSchema, context: TesterContext) => {
      const schemaPath = (uischema as ControlElement).scope;
      const resolvedSchema = resolveSchema(
        schema,
        schemaPath,
        context?.rootSchema ?? schema,
      ) as AnnotatedJsonSchema;
      return Boolean(
        resolvedSchema['x-entity-control']?.type === 'json-logic' &&
          resolvedSchema['x-entity-control']?.options?.jsonLogic
            ?.isRuleTemplate,
      );
    },
  ),
);

// type: number
export const isNumberInputControl = rankWith(TesterRank.Base, isNumberControl);

// type: integer
export const isIntegerInputControl = rankWith(
  TesterRank.Base,
  isIntegerControl,
);

// type: boolean
export const isBooleanInputControl = rankWith(
  TesterRank.Base,
  isBooleanControl,
);

export const isDataPointInputControl = rankWith(
  TesterRank.Higher,
  and(
    isStringControl,
    (uischema: UISchemaElement, schema: JsonSchema, context: TesterContext) => {
      const schemaPath = (uischema as ControlElement).scope;
      const resolvedSchema = resolveSchema(
        schema,
        schemaPath,
        context?.rootSchema ?? schema,
      ) as AnnotatedJsonSchema;
      return Boolean(resolvedSchema['x-entity-data-point']);
    },
  ),
);

// asset uri control matches uri-format image/pdf assets
// (value stored as a url instead of the domainEntityId)
export const isAssetUriControl = rankWith(
  TesterRank.Highest + 1,
  and(
    isStringControl,
    (uischema: UISchemaElement, schema: JsonSchema, context: TesterContext) => {
      const schemaPath = (uischema as ControlElement).scope;
      const resolvedSchema = resolveSchema(
        schema,
        schemaPath,
        context?.rootSchema ?? schema,
      ) as AnnotatedJsonSchema;
      return (
        (resolvedSchema['x-entity-asset']?.type === 'image' ||
          resolvedSchema['x-entity-asset']?.type === 'pdf') &&
        (resolvedSchema.format === 'uri-reference' ||
          resolvedSchema.format === 'uri' ||
          resolvedSchema.format === 'uri-template')
      );
    },
  ),
);

export const isAssetFileControl = rankWith(
  TesterRank.Highest,
  and(
    isStringControl,
    schemaMatches((schema) => {
      const control = (schema as any)['x-entity-control'];
      return control?.type === 'assetFile';
    }),
  ),
);

/**
 * Object Control Testers
 */

// type: object
export const isObjectDispatchControl = rankWith(
  TesterRank.Base,
  isObjectControl,
);

export const isObjectCard = rankWith(
  TesterRank.Highest,
  schemaMatches(
    (schema) =>
      (schema as AnnotatedJsonSchema)['x-entity-control']?.type ===
      'objectCard',
  ),
);

export const isConditionalActionControl = rankWith(
  TesterRank.Highest,
  (uischema: UISchemaElement, schema: JsonSchema, context: TesterContext) => {
    // Resolve the schema at the ui-element's scope so we look at the
    // property's own schema, not the root. Mirrors the manual pattern used
    // by `isRuleTemplateJsonLogicInputControl` rather than `schemaMatches`
    // (which requires the uischema be a `Control`).
    const { scope } = uischema as ControlElement;
    let resolvedSchema: AnnotatedJsonSchema | undefined;
    try {
      resolvedSchema = resolveSchema(
        schema,
        scope,
        context?.rootSchema ?? schema,
      ) as AnnotatedJsonSchema | undefined;
    } catch {
      resolvedSchema = undefined;
    }
    return resolvedSchema?.['x-entity-control']?.type === 'conditionalAction';
  },
);

/**
 * Array Control Testers
 */

export const isArrayObjectPrimitiveDispatchControl = rankWith(
  TesterRank.Highest,
  schemaMatches((schema: AnnotatedJsonSchema) => {
    if (schema.type !== 'array' || !schema.items) return false;
    const itemSchema = schema.items as AnnotatedJsonSchema;
    if (
      schema['x-entity-control']?.type === 'flattenLayout' &&
      schema['x-entity-control']?.options?.flattenLayout?.type ===
        'primitiveControl'
    ) {
      return true;
    }
    if (
      isCombinatorObjectSchema(itemSchema) ||
      isCombinatorArraySchema(schema as AnnotatedJsonSchema)
    ) {
      return false;
    }

    if (itemSchema['x-entity-data-source']?.dataSource) return false;

    return isPrimitiveObjectSchema(itemSchema);
  }),
);

export const isArrayObjectComplexDispatchControl = rankWith(
  TesterRank.Highest,

  schemaMatches((schema: AnnotatedJsonSchema) => {
    if (schema.type !== 'array' || !schema.items) {
      return false;
    }

    const itemSchema = schema.items as AnnotatedJsonSchema;
    // Exclude flattenLayout 'primitiveControl' since that will be handled by the primitive control
    if (
      schema['x-entity-control']?.type === 'flattenLayout' &&
      schema['x-entity-control']?.options?.flattenLayout?.type ===
        'primitiveControl'
    )
      return false;

    const isItemSchemaCombinatorOrComplexObjectSchema =
      itemSchema.type === 'object' &&
      (!isPrimitiveObjectSchema(itemSchema) ||
        isCombinatorObjectSchema(itemSchema));

    return (
      isItemSchemaCombinatorOrComplexObjectSchema ||
      isCombinatorArraySchema(schema as AnnotatedJsonSchema)
    );
  }),
);

export const isArrayPrimitiveDispatchControl = rankWith(
  TesterRank.Higher,
  isPrimitiveArrayControl,
);

export const isCheckboxGroupControl = rankWith(
  TesterRank.Highest + 1,
  (uischema: UISchemaElement, schema: JsonSchema, context: TesterContext) => {
    const schemaPath = (uischema as ControlElement).scope;
    const resolvedSchema = resolveSchema(
      schema,
      schemaPath,
      context?.rootSchema ?? schema,
    ) as AnnotatedJsonSchema;

    return Boolean(
      resolvedSchema.type === 'array' &&
        resolvedSchema['x-entity-control']?.type === 'checkboxGroup',
    );
  },
);

/**
 * Entity Reference
 */

const isMediaAssetType = (
  uischema: UISchemaElement,
  schema: JsonSchema,
  context: TesterContext,
) => {
  const schemaPath = (uischema as ControlElement).scope;
  const resolvedSchema = resolveSchema(
    schema,
    schemaPath,
    context?.rootSchema ?? schema,
  ) as AnnotatedJsonSchema;

  const isUriValue = URI_FORMATS.includes(resolvedSchema.format ?? '');
  return (
    (resolvedSchema['x-entity-asset']?.type === 'image' ||
      resolvedSchema['x-entity-asset']?.type === 'pdf') &&
    !isUriValue
  );
};

export const isMediaReferenceInputControl = rankWith(
  TesterRank.Highest + 1,
  and(isStringControl, isMediaAssetType),
);

export const isEntityReferenceInjectedControl = rankWith(
  TesterRank.Highest + 1,
  (uischema, schema, context) => {
    const schemaPath = (uischema as ControlElement).scope;
    const resolvedSchema = resolveSchema(
      schema,
      schemaPath,
      context?.rootSchema ?? schema,
    ) as AnnotatedJsonSchema;
    const { 'x-entity-reference': entityReference } = resolvedSchema;
    return Boolean(entityReference && resolvedSchema.type === 'object');
  },
);

export const isEntityReferenceStringControl = rankWith(
  TesterRank.Highest + 1,
  (uischema, schema, context) => {
    const schemaPath = (uischema as ControlElement).scope;
    const resolvedSchema = resolveSchema(
      schema,
      schemaPath,
      context?.rootSchema ?? schema,
    ) as AnnotatedJsonSchema;
    const { 'x-entity-reference': entityReference } = resolvedSchema;
    return Boolean(entityReference && resolvedSchema.type === 'string');
  },
);

/**
 * Matches only arrays whose items are relationship-style entity references
 * (x-entity-reference.relationship). Arrays of ID-style references
 * (x-entity-reference.domainEntityType) are handled by the primitive array
 * control so each item uses EntitySelectControl with the domain-entity wrapper.
 */
export const isArrayEntityReferenceControl = rankWith(
  TesterRank.Highest,
  (uischema: UISchemaElement, schema: JsonSchema, context: TesterContext) => {
    const schemaPath = (uischema as ControlElement).scope;
    const resolvedSchema = resolveSchema(
      schema,
      schemaPath,
      context?.rootSchema ?? schema,
    );
    if (resolvedSchema.type !== 'array' || !resolvedSchema.items) return false;

    const itemsSchema = resolvedSchema?.items as AnnotatedJsonSchema;
    const { 'x-entity-reference': entityReference } = itemsSchema;
    return Boolean(
      entityReference && isRelationshipEntityReference(entityReference),
    );
  },
);

export const isJsonLogicArrayControl = rankWith(
  TesterRank.Highest,
  (uischema: UISchemaElement, schema: JsonSchema, context: TesterContext) => {
    const schemaPath = (uischema as ControlElement).scope;
    const resolvedSchema = resolveSchema(
      schema,
      schemaPath,
      context?.rootSchema ?? schema,
    );
    if (resolvedSchema.type !== 'array' || !resolvedSchema.items) return false;

    const itemsSchema = resolvedSchema?.items as AnnotatedJsonSchema;
    return Boolean(itemsSchema['x-entity-control']?.type === 'json-logic');
  },
);
/**
 * Combinator Control Testers
 */

// The built-in isOneOfControl testers are specifically for arrays
// so we need to add these custom testers for oneOf objects

const isOneOfControl = (
  uischema: UISchemaElement,
  schema: JsonSchema,
  context: TesterContext,
): boolean => {
  const schemaPath = (uischema as ControlElement).scope;
  const resolvedSchema = resolveSchema(
    schema,
    schemaPath,
    context?.rootSchema ?? schema,
  );

  return Boolean(resolvedSchema.oneOf);
};

export const isCombinatorObjectInputControl = rankWith(
  TesterRank.Higher,
  and(isObjectControl, isOneOfControl),
);

export const isCombinatorArrayItemPageControl = rankWith(
  TesterRank.Base,
  isCombinatorArrayOfComplexObjects,
);

export const isCombinatorOneOfArrayInputControl = rankWith(
  TesterRank.Base,
  and(schemaTypeIs('array'), isOneOfControl),
);
